import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '@/App';
import D2AnalysisView from '@/pages/D2AnalysisView';
import {
  ArrowLeft, Download, Sparkles, BookOpen, ChevronDown,
  BarChart3, GitFork, Dna, Layers, Target, TrendingUp, Grid3x3, FlaskConical, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { parseAxiosError } from '@/utils/axiosErrors';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ClusterAnalysisView from './ClusterAnalysisView';
import SharedNav from '@/components/SharedNav';
import { FullExportBar } from '@/components/results/ExportButtons';
import { Phase1ResultPanels } from '@/components/Phase1UploadCard';
import { Phase2ResultPanels } from '@/components/Phase2UploadCard';
import { Phase3ResultPanels } from '@/components/Phase3UploadCard';
import { Phase4ResultPanels } from '@/components/Phase4UploadCard';
import { CrossPhaseContinuityBanner } from '@/components/CrossPhaseContinuityBanner';
import { ClassroomToggle } from '@/components/results/ClassroomToggle';
import { ANALYSIS_META, NAV_GROUPS } from '@/components/results/resultsMeta';
import FigurePreviewPanel from '@/components/results/FigurePreviewPanel';
import GenerationMeanSection from '@/components/GenerationMeanSection';
import GeneralAnalysisEnhancedView from '@/components/results/GeneralAnalysisEnhancedView';
import { PooledAnovaView, ManovaEnhancedView, FullMETView, GenotypeYearView } from '@/components/results/METModuleViews';
import {
  DoseResponseView, LD50GR50View, MutationFrequencyView, MutantSpectrumView, M2GenerationView,
  ChlorophyllMutationView, QuantitativeMutationView, MutationRateView, ComparativeMutagenView,
  M3ProgenyView, MutantScreeningIndexView,
} from '@/components/results/MutationBreedingViews';
import {
  AudpcView, InfectionTypeView, DiseaseYieldView, VirulenceFrequencyView, ResistanceScreeningView,
} from '@/components/results/DiseaseResistanceViews';
import {
  CropGrowthView, PhotosynthesisView, WaterRelationsView, ChlorophyllView, EnzymeActivityView, OxidativeStressView,
} from '@/components/results/PhysiologyViews';
import {
  NutritionalQualityView, ProteinAminoAcidView, OilFattyAcidView, AntioxidantView, HormoneView, SecondaryMetabolitesView,
} from '@/components/results/PhysiologyViewsPhase2';
import { IntegratedToleranceView, BiofortificationView } from '@/components/results/PhysiologyPhase3';
import PhysioFigureQualityChecker from '@/components/results/PhysioFigureQualityChecker';
import {
  SoilPhysicalView, SoilChemicalView, SoilSTCRView, SoilHealthIndexView,
  SoilNutrientAvailView, SoilMicrobialView, SoilCarbonView, SoilQualityIndexView,
  SoilFertilizerResponseView, SoilLandUseChangeView,
} from '@/components/results/SoilScienceViews';
import AugmentedMETView from '@/components/results/AugmentedMETView';
import QualitativeDUSView from '@/components/results/QualitativeDUSView';
import { CorrelationView, RegressionView, MultipleCorrelationView, MultipleRegressionView } from '@/components/results/CorrelationRegressionViews';
import { PathAnalysisView } from '@/components/results/PathAnalysisViews';
import { PCAView, HTPView, MolecularBreedingView } from '@/components/results/MultivariateMiscViews';
import { RBDView, NCDesignView } from '@/components/results/ClassicalDesignViews';
import { AMMIView } from '@/components/results/AMMIResultView';
import { EberhartView, GGEBiplotView, METView } from '@/components/results/StabilityGGEViews';
import { LxTView, DiallelView } from '@/components/results/LxTDiallelViews';
import { HeritabilityView, SelectionIndexView } from '@/components/results/HeritabilitySelectionViews';
import { NonParametricView, DiversityView } from '@/components/results/NonParamDiversityViews';
import { GenericAnalysisView } from '@/components/results/GenericAnalysisResultView';

/* Icon resolver for metadata */
const ICON_MAP = { Grid3x3, TrendingUp, Dna, GitFork, Target, Layers, FlaskConical, BarChart3, BookOpen };
function getMeta(key) {
  const raw = ANALYSIS_META[key];
  if (!raw) return { label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), icon: BarChart3, color: '#6B8E7B' };
  return { label: raw.label, icon: ICON_MAP[raw.icon] || BarChart3, color: raw.color };
}

/* ============================================================
   CORRELATION VIEW
============================================================ */
function EnhancedResults() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  // Sidebar collapsible state — keyed by NAV_GROUPS.id. The group
  // containing the active tab is auto-expanded. Other groups stay
  // collapsed for a clean overview.
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const { data } = await axios.get(`${API}/analysis/${id}`, { withCredentials: true });
        setAnalysis(data);
        const keys = Object.keys(data.results || {}).filter(k => typeof data.results[k] === 'object' && !data.results[k]?.error);
        if (keys.length > 0 && !activeTab) {
          // Auto-select the actual analysis tab, not the "Input Summary" /
          // "Data Adequacy" meta panels which are the first keys on
          // upload-and-analyze runs. Prefer the run's declared analysis_type
          // when it maps to a real result key; else the first non-meta tab.
          const META_KEYS = ['input_summary', 'data_adequacy'];
          const at = data.analysis_type;
          const preferred =
            (at && keys.includes(at)) ? at
              : (keys.find(k => !META_KEYS.includes(k)) || keys[0]);
          setActiveTab(preferred);
        }
      } catch (e) {
        toast.error('Failed to load analysis results');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchAnalysis();
  }, [id]);

  const [exporting, setExporting] = useState(null);
  const [excludedFigures, setExcludedFigures] = useState([]);

  const handleDownload = async (format, section = '') => {
    const exportKey = section ? `${section}-${format}` : `full-${format}`;
    setExporting(exportKey);
    const label = section ? `${getMeta(section).label} ${format.toUpperCase()}` : `Full Report ${format.toUpperCase()}`;
    toast.info(`Generating ${label}...`);
    try {
      const response = await axios.post(
        `${API}/generate-document/${id}`,
        { format, section, excluded_figures: excludedFigures },
        // B-NEW-3 FIX-3A (Niranjan, Feb 2026): 90 s ceiling so a
        // hung request can't permanently disable the export buttons.
        // Bumped from 30 s in Sub-Batch 3W (Jun 2026): General-Analysis
        // DOCX on a 50-genotype × 7-predictor dataset takes 25–35 s
        // server-side + blob streaming, easily blowing past 30 s and
        // surfacing the user-reported "DOCX/XLSX downloads not working"
        // bug.  `finally` block below always resets `exporting` to null.
        { withCredentials: true, responseType: 'blob', timeout: 90000 }
      );
      // Sub-Batch 3W: explicit MIME on Blob so Windows / macOS Finder
      // associates the saved file with the right opener (Word, Excel,
      // Acrobat). Without an explicit type some browsers (Edge in
      // particular) fall back to .download being ignored and open the
      // raw bytes inline as text.
      const mimeMap = {
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pdf: 'application/pdf',
      };
      const blobType = mimeMap[format] || response.headers?.['content-type'] || 'application/octet-stream';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
      const link = document.createElement('a');
      link.href = url;
      const sectionLabel = section ? section.replace(/_/g, '-') : 'Full_Report';
      link.setAttribute('download', `BKAgriD_${sectionLabel}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} downloaded!`);
    } catch (e) {
      // B-NEW-2/3 fix: blob responses may carry a JSON `detail` body.
      // axios timeout uses code === 'ECONNABORTED' — surface that as
      // a clear "try again" message instead of generic "Download failed".
      if (e?.code === 'ECONNABORTED') {
        toast.error('Export timed out. Please try again. If the issue persists, refresh the page (Ctrl+Shift+R).');
      } else {
        const detail = await parseAxiosError(e, 'Download failed.');
        toast.error(detail);
      }
    } finally {
      setExporting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: 'var(--green-500)', borderTopColor: 'transparent' }} />
          <p className="font-body" style={{ fontSize: 14, color: 'var(--text-medium)' }}>Loading analysis results...</p>
        </div>
      </div>
    );
  }

  const results = analysis?.results || {};
  // Sections intentionally hidden from the Results tab nav even when
  // present in the backend response. `cluster_analysis` and
  // `d2_analysis` produce overlapping dendrogram + Tocher output;
  // breeders only need one tab — D² & Cluster is the comprehensive
  // one. (Niranjan, Feb 2026)
  const HIDDEN_SECTIONS = new Set(['cluster_analysis']);
  const sectionKeys = Object.keys(results).filter(k => typeof results[k] === 'object' && results[k] !== null && !results[k].error && !HIDDEN_SECTIONS.has(k));

  const renderContent = (key) => {
    const data = results[key];
    if (!data) return null;

    switch (key) {
      case 'correlation': return <CorrelationView data={data} />;
      case 'multiple_correlation': return <MultipleCorrelationView data={data} />;
      case 'regression': return <RegressionView data={data} />;
      case 'multiple_regression': return <MultipleRegressionView data={data} />;
      case 'path_analysis': return <PathAnalysisView data={data} />;
      case 'general_analysis_enhanced': return <GeneralAnalysisEnhancedView data={data} analysisId={id} />;
      case 'pca': return <PCAView data={data} />;
      case 'htp_analysis': return <HTPView data={data} />;
      case 'molecular_breeding': return <MolecularBreedingView data={data} />;
      case 'ammi': return <AMMIView data={data} analysisId={id} />;
      case 'gge_biplot': return <GGEBiplotView data={data} />;
      case 'eberhart_russell_stability': return <EberhartView data={data} />;
      case 'eberhart_russell': return <EberhartView data={data} />;
      case 'line_x_tester': return <LxTView data={data} />;
      case 'diallel_griffing': return <DiallelView data={data} />;
      case 'met_analysis': return <METView data={data} />;
      case 'rbd_analysis': return <RBDView data={data} />;
      case 'crd_analysis': return <RBDView data={data} />;
      case 'nc_design_ii': return <NCDesignView data={data} />;
      case 'nc_design_i': return <NCDesignView data={data} />;
      case 'nc_design_iii': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'generation_mean':
        // Cavalli (1952) + Hayman (1958) when the new full-analysis
        // shape is present (three_param_model + scaling_tests). Falls
        // back to the generic dump for the legacy shape.
        if (data && data.three_param_model) {
          return <GenerationMeanSection data={data} />;
        }
        return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'strip_plot': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'latin_square': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'alpha_lattice': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'missing_plot': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'strip_split_plot': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'bib_design': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'nested_design': return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'augmented':
      case 'augmented_design':
        // Sub-Batch 3T — MET routing: if the result carries a 'scenario'
        // field (A or B) or `refused: true`, render the MET view; else
        // fall through to the existing per-trait augmented renderer.
        if (data && (data.scenario === 'A' || data.scenario === 'B' || data.refused)) {
          return <AugmentedMETView data={data} />;
        }
        return <GenericAnalysisView data={data} sectionKey={key} />;
      case 'qualitative_dus': return <QualitativeDUSView data={data} />;
      case 'heritability_ga': return <HeritabilityView data={data} />;
      case 'selection_index': return <SelectionIndexView data={data} />;
      case 'non_parametric': return <NonParametricView data={data} />;
      case 'diversity': return <DiversityView data={data} />;
      case 'pooled_anova': return <PooledAnovaView data={data} analysisId={id} />;
      case 'manova_enhanced': return <ManovaEnhancedView data={data} analysisId={id} />;
      case 'full_met': return <FullMETView data={data} analysisId={id} />;
      case 'genotype_year': return <GenotypeYearView data={data} analysisId={id} />;
      case 'mutation_dose_response': return <DoseResponseView data={data} />;
      case 'mutation_ld50_gr50': return <LD50GR50View data={data} />;
      case 'mutation_frequency': return <MutationFrequencyView data={data} />;
      case 'mutation_spectrum': return <MutantSpectrumView data={data} />;
      case 'mutation_m2_generation': return <M2GenerationView data={data} />;
      case 'mutation_chlorophyll': return <ChlorophyllMutationView data={data} />;
      case 'mutation_quantitative': return <QuantitativeMutationView data={data} />;
      case 'mutation_rate': return <MutationRateView data={data} />;
      case 'mutation_comparative': return <ComparativeMutagenView data={data} />;
      case 'mutation_m3_progeny': return <M3ProgenyView data={data} />;
      case 'mutation_screening_index': return <MutantScreeningIndexView data={data} />;
      case 'disease_audpc': return <AudpcView data={data} />;
      case 'disease_infection_type': return <InfectionTypeView data={data} />;
      case 'disease_yield_correlation': return <DiseaseYieldView data={data} />;
      case 'disease_virulence_frequency': return <VirulenceFrequencyView data={data} />;
      case 'disease_screening_summary': return <ResistanceScreeningView data={data} />;
      case 'physio_crop_growth': return <><PhysioFigureQualityChecker analysisType="physio_crop_growth" data={data} /><CropGrowthView data={data} /></>;
      case 'physio_photosynthesis': return <><PhysioFigureQualityChecker analysisType="physio_photosynthesis" data={data} /><PhotosynthesisView data={data} /></>;
      case 'physio_water_relations': return <><PhysioFigureQualityChecker analysisType="physio_water_relations" data={data} /><WaterRelationsView data={data} /></>;
      case 'physio_chlorophyll_pigments': return <ChlorophyllView data={data} />;
      case 'physio_enzyme_activity': return <><PhysioFigureQualityChecker analysisType="physio_enzyme_activity" data={data} /><EnzymeActivityView data={data} /></>;
      case 'physio_oxidative_stress': return <><PhysioFigureQualityChecker analysisType="physio_oxidative_stress" data={data} /><OxidativeStressView data={data} /></>;
      case 'physio_nutritional_quality': return <NutritionalQualityView data={data} />;
      case 'physio_protein_aa': return <ProteinAminoAcidView data={data} />;
      case 'physio_oil_fa': return <OilFattyAcidView data={data} />;
      case 'physio_antioxidant': return <AntioxidantView data={data} />;
      case 'physio_hormones': return <HormoneView data={data} />;
      case 'physio_secondary_metabolites': return <SecondaryMetabolitesView data={data} />;
      case 'physio_integrated_tolerance': return <><PhysioFigureQualityChecker analysisType="physio_integrated_tolerance" data={data} /><IntegratedToleranceView data={data} /></>;
      case 'physio_biofortification': return <><PhysioFigureQualityChecker analysisType="physio_biofortification" data={data} /><BiofortificationView data={data} /></>;
      case 'soil_physical': return <SoilPhysicalView data={data} />;
      case 'molecular_phase1': return <Phase1ResultPanels result={data} />;
      case 'molecular_qtl': return (
        <>
          <CrossPhaseContinuityBanner currentAnalysisId={id} currentPhase={2} />
          <Phase2ResultPanels result={data} />
        </>
      );
      case 'molecular_gwas': return (
        <>
          <CrossPhaseContinuityBanner currentAnalysisId={id} currentPhase={3} />
          <Phase3ResultPanels result={data} />
        </>
      );
      case 'molecular_genomic_selection': return (
        <>
          <CrossPhaseContinuityBanner currentAnalysisId={id} currentPhase={4} />
          <Phase4ResultPanels result={data} />
        </>
      );
      case 'soil_chemical': return <SoilChemicalView data={data} />;
      case 'soil_stcr': return <SoilSTCRView data={data} />;
      case 'soil_health_index': return <SoilHealthIndexView data={data} />;
      case 'soil_nutrient_availability': return <SoilNutrientAvailView data={data} />;
      case 'soil_microbial': return <SoilMicrobialView data={data} />;
      case 'soil_carbon': return <SoilCarbonView data={data} />;
      case 'soil_quality_index': return <SoilQualityIndexView data={data} />;
      case 'soil_fertilizer_response': return <SoilFertilizerResponseView data={data} />;
      case 'soil_land_use_change': return <SoilLandUseChangeView data={data} />;
      case 'cluster_analysis':
        return data.tocher ? <ClusterAnalysisView data={data} /> : <GenericAnalysisView data={data} sectionKey={key} />;
      case 'd2_analysis':
        return data.pca_3d_data ? <D2AnalysisView data={data} /> : <GenericAnalysisView data={data} sectionKey={key} />;
      default: return <GenericAnalysisView data={data} sectionKey={key} />;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--page-bg)' }} data-testid="results-page">
      <SharedNav />

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Title + Full Export */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display" style={{ fontSize: 24, color: 'var(--green-900)' }} data-testid="results-title">
              {(analysis?.analysis_type || 'general').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Results
            </h2>
            <p className="font-body mt-1" style={{ fontSize: 13, color: 'var(--text-medium)' }} data-testid="filename">
              {analysis?.filename} &bull; {new Date(analysis?.created_at).toLocaleString()} &bull; {sectionKeys.length} analyses
            </p>
          </div>
          <FullExportBar exporting={exporting} onDownload={handleDownload} />
        </div>

        {/* Figure Preview Panel */}
        <FigurePreviewPanel results={results} onExcludedChange={setExcludedFigures} />

        {/* Tabbed Navigation — left-sidebar layout matching the
            Designs page (Niranjan, Feb 2026). Collapsible NAV_GROUPS
            on a 280px sidebar; tab content on the right in a sticky
            scroll-friendly column. */}
        {sectionKeys.length > 0 ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex flex-col lg:flex-row gap-6 items-start">
              {/* Left sidebar */}
              <div className="w-full lg:w-[280px] lg:shrink-0 lg:sticky lg:top-[80px]" data-testid="results-sidebar">
                <TabsList className="flex flex-col h-auto p-3 bg-white border border-[#E8E6E1] rounded-2xl shadow-sm gap-1.5 w-full" data-testid="grouped-nav">
                  {NAV_GROUPS.map(group => {
                    const groupTabs = group.keys.filter(k => sectionKeys.includes(k));
                    const hasPlaceholders = group.placeholders && group.placeholders.length > 0;
                    if (groupTabs.length === 0 && !hasPlaceholders) return null;
                    const isActiveGroup = groupTabs.includes(activeTab);
                    // Default-open: groups with the active tab. Other
                    // groups respect explicit user toggles in openGroups.
                    const isOpen = openGroups[group.id] !== undefined ? openGroups[group.id] : isActiveGroup;
                    return (
                      <div key={group.id} className="w-full" data-testid={`nav-group-${group.id}`}>
                        <button
                          type="button"
                          onClick={() => setOpenGroups(prev => ({ ...prev, [group.id]: !isOpen }))}
                          className="w-full flex items-center gap-2 transition-colors rounded-lg"
                          style={{
                            padding: '9px 12px',
                            textAlign: 'left',
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            background: isOpen ? '#F9F8F6' : 'transparent',
                            color: isActiveGroup ? group.color : '#5E5A56',
                            border: isOpen ? '1px solid #E8E6E1' : '1px solid transparent',
                          }}
                          data-testid={`group-label-${group.id}`}
                        >
                          <ChevronDown
                            size={13}
                            style={{
                              transition: 'transform 0.2s',
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              color: '#8A8580',
                            }}
                          />
                          <span className="flex-1 truncate">{group.label}</span>
                          {(groupTabs.length + (group.placeholders?.length || 0)) > 0 && (
                            <span className="text-[10px] font-bold tabular-nums" style={{ color: '#B0ACA8' }}>
                              {groupTabs.length}
                            </span>
                          )}
                        </button>
                        {isOpen && (
                          <div className="flex flex-col gap-0.5 mt-1 mb-2 pl-1">
                            {groupTabs.map(key => {
                              const meta = getMeta(key);
                              const Icon = meta.icon;
                              const isActive = activeTab === key;
                              return (
                                <TabsTrigger
                                  key={key}
                                  value={key}
                                  className="flex items-center gap-2 justify-start px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap text-left w-full"
                                  style={isActive
                                    ? { backgroundColor: meta.color, color: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }
                                    : { backgroundColor: 'transparent', color: '#5E5A56' }
                                  }
                                  data-testid={`tab-${key}`}
                                >
                                  <Icon size={14} style={{ color: isActive ? 'white' : meta.color, flexShrink: 0 }} />
                                  <span className="truncate">{meta.label}</span>
                                </TabsTrigger>
                              );
                            })}
                            {group.placeholders && group.placeholders.map(ph => (
                              <span
                                key={ph}
                                className="flex items-center gap-2 justify-start px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap border border-dashed border-[#D5D3CF] text-[#B0ACA8] cursor-default select-none"
                                data-testid={`placeholder-${ph.toLowerCase().replace(/\s/g, '-')}`}
                              >
                                <span className="truncate">{ph}</span>
                                <span className="ml-auto text-[8px] bg-[#E8E6E1] text-[#8A8580] rounded px-1 py-0.5 leading-none">Soon</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* Ungrouped tabs (fallback for anything not in NAV_GROUPS) */}
                  {(() => {
                    const allGroupedKeys = NAV_GROUPS.flatMap(g => g.keys);
                    const ungrouped = sectionKeys.filter(k => !allGroupedKeys.includes(k));
                    if (ungrouped.length === 0) return null;
                    const isActiveGroup = ungrouped.includes(activeTab);
                    const isOpen = openGroups['__other'] !== undefined ? openGroups['__other'] : isActiveGroup;
                    return (
                      <div className="w-full" data-testid="nav-group-other">
                        <button
                          type="button"
                          onClick={() => setOpenGroups(prev => ({ ...prev, __other: !isOpen }))}
                          className="w-full flex items-center gap-2 transition-colors rounded-lg"
                          style={{
                            padding: '9px 12px',
                            textAlign: 'left',
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            background: isOpen ? '#F9F8F6' : 'transparent',
                            color: isActiveGroup ? '#5E5A56' : '#8A8580',
                            border: isOpen ? '1px solid #E8E6E1' : '1px solid transparent',
                          }}
                        >
                          <ChevronDown
                            size={13}
                            style={{
                              transition: 'transform 0.2s',
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              color: '#8A8580',
                            }}
                          />
                          <span className="flex-1 truncate">Other</span>
                          <span className="text-[10px] font-bold tabular-nums" style={{ color: '#B0ACA8' }}>{ungrouped.length}</span>
                        </button>
                        {isOpen && (
                          <div className="flex flex-col gap-0.5 mt-1 mb-2 pl-1">
                            {ungrouped.map(key => {
                              const meta = getMeta(key);
                              const Icon = meta.icon;
                              const isActive = activeTab === key;
                              return (
                                <TabsTrigger
                                  key={key}
                                  value={key}
                                  className="flex items-center gap-2 justify-start px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap text-left w-full"
                                  style={isActive
                                    ? { backgroundColor: meta.color, color: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' }
                                    : { backgroundColor: 'transparent', color: '#5E5A56' }
                                  }
                                  data-testid={`tab-${key}`}
                                >
                                  <Icon size={14} style={{ color: isActive ? 'white' : meta.color, flexShrink: 0 }} />
                                  <span className="truncate">{meta.label}</span>
                                </TabsTrigger>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </TabsList>
              </div>

              {/* Right content column */}
              <div className="flex-1 min-w-0 w-full">
                {sectionKeys.map(key => (
                  <TabsContent key={key} value={key} className="mt-0">
                    <Card className="border border-[#E8E6E1] shadow-sm rounded-2xl">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <CardTitle className="flex items-center gap-3 text-lg" data-testid={`section-title-${key}`}>
                            {React.createElement(getMeta(key).icon, { size: 22, style: { color: getMeta(key).color } })}
                            {getMeta(key).label}
                          </CardTitle>
                          <div className="flex items-center gap-1.5" data-testid={`section-download-${key}`}>
                            {['docx', 'xlsx', 'pdf'].map(fmt => (
                              <button
                                key={fmt}
                                onClick={() => handleDownload(fmt, key)}
                                disabled={!!exporting}
                                title={`Download ${getMeta(key).label} as ${fmt.toUpperCase()}`}
                                className="relative flex items-center gap-1 text-[10px] font-semibold rounded-full px-3 py-1.5 transition-all border shadow-sm disabled:opacity-50"
                                style={{
                                  borderColor: getMeta(key).color + '40',
                                  color: getMeta(key).color,
                                  backgroundColor: getMeta(key).color + '0A',
                                }}
                                data-testid={`download-${key}-${fmt}-button`}
                              >
                                {exporting === `${key}-${fmt}` ? (
                                  <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                                ) : (
                                  <Download size={11} />
                                )}
                                {fmt.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {renderContent(key)}
                        <ClassroomToggle sectionKey={key} />
                      </CardContent>
                    </Card>
                  </TabsContent>
                ))}
              </div>
            </div>
          </Tabs>
        ) : (
          <div className="text-center py-20 text-[#8A8580]">
            <BarChart3 size={48} className="mx-auto mb-4 opacity-40" />
            <p>No analysis results available.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default EnhancedResults;
